/**
 * **Sound an/aus** — ein Weg für beide Plattformen. Audio-Engine und
 * Einstellungs-Schalter lesen und schreiben nur hierüber.
 *
 * - **Electron:** Der Main-Prozess hält den Wert (`settings.json`), weil jedes
 *   Fenster seinen eigenen Speicher hat. Er sagt eine Änderung allen Fenstern
 *   per `sounds-changed` an; auch der Tray-Haken geht diesen Weg.
 * - **Web:** ein Fenster, der Browser merkt sich den Wert. Mehrere Tabs
 *   gleichen sich über das `storage`-Ereignis ab, das nur die ANDEREN Tabs
 *   bekommen — der eigene hört über `localListeners`.
 *
 * Bis v0.6.8 gab es nur den Electron-Weg; im Web spielte die App immer.
 */

const STORAGE_KEY = 'happytask.soundsEnabled'

type Listener = (enabled: boolean) => void
const localListeners = new Set<Listener>()

function mainApi() {
  if (typeof window === 'undefined') return null
  // Laut Typ immer da — im Web (und in Storybook) fehlt es aber.
  const api = (window as { api?: Window['api'] }).api
  return typeof api?.invoke === 'function' ? api : null
}

function readLocal(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    // Gesperrter Speicher (privater Modus): lieber Klang als ein Absturz.
    return true
  }
}

export async function getSoundsEnabled(): Promise<boolean> {
  const api = mainApi()
  if (!api) return readLocal()
  try {
    return (await api.invoke('sounds:get')) !== false
  } catch {
    return true
  }
}

export function setSoundsEnabled(enabled: boolean): void {
  const api = mainApi()
  if (api) {
    void Promise.resolve(api.invoke('sounds:set', enabled)).catch(() => {})
    return
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, String(enabled))
  } catch {
    // Nicht gemerkt, gilt aber für diese Sitzung.
  }
  for (const listener of localListeners) listener(enabled)
}

/** Meldet jede Änderung, auch die aus einem anderen Fenster oder Tab. Gibt die Abmeldung zurück. */
export function onSoundsChanged(listener: Listener): () => void {
  const api = mainApi()
  if (api) {
    const off = api.receive?.('sounds-changed', (enabled: boolean) => listener(enabled !== false))
    return () => { if (typeof off === 'function') off() }
  }

  localListeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener(e.newValue !== 'false')
  }
  window.addEventListener('storage', onStorage)
  return () => {
    localListeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}
